<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Confirmados</h1>
      <div class="confirmed-counter">
        <span class="counter-num">{{ confirmedCount }}</span>
        <span class="counter-label">confirmado{{ confirmedCount === 1 ? '' : 's' }}</span>
      </div>
    </div>

    <div class="search-box">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="search-icon"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0c.41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z"/></svg>
      <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar confirmado..." />
      <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''" aria-label="Limpar busca">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
      </button>
    </div>

    <div class="filters">
      <div class="filter-row">
        <span class="filter-label">Transporte</span>
        <div class="chip-row">
          <button
            class="filter-chip"
            :class="{ active: transporteFilter === 'ALL' }"
            @click="transporteFilter = 'ALL'"
          >Todos</button>
          <button
            v-for="t in transportes"
            :key="t.value"
            class="filter-chip"
            :class="{ active: transporteFilter === t.value }"
            :style="transporteFilter === t.value
              ? { background: t.hex, color: '#fff', borderColor: t.hex }
              : { borderColor: t.hex, color: t.hex }"
            @click="transporteFilter = t.value"
          >{{ t.label }}</button>
        </div>
      </div>

      <div class="filter-row">
        <span class="filter-label">Equipe</span>
        <div class="chip-row">
          <button
            class="filter-chip"
            :class="{ active: equipeFilter === 'ALL' }"
            @click="equipeFilter = 'ALL'"
          >Todas</button>
          <button
            v-for="equipe in equipes"
            :key="equipe.value"
            class="filter-chip"
            :class="{ active: equipeFilter === equipe.value }"
            :style="equipeFilter === equipe.value
              ? { background: equipe.hex, color: equipe.textColor, borderColor: equipe.mainColor }
              : { borderColor: equipe.mainColor, color: equipe.mainColor }"
            @click="equipeFilter = equipe.value"
          >{{ equipe.label }}</button>
          <button
            v-if="semEquipeConfirmedCount > 0"
            class="filter-chip filter-chip-sem-equipe"
            :class="{ active: equipeFilter === 'NONE' }"
            @click="equipeFilter = 'NONE'"
          >Sem equipe</button>
        </div>
      </div>
    </div>

    <SkeletonLoader v-if="store.loading && store.acampantes.length === 0" variant="table-row" :count="6" />

    <div class="confirmed-list" v-else-if="filtered.length > 0">
      <div class="confirmed-item card" v-for="item in filtered" :key="item.id">
        <div class="item-main">
          <div class="item-name-row">
            <span class="item-name">{{ item.name }}</span>
            <EquipeChip :equipe="item.equipe" />
            <TransporteBadge :value="item.transporte" />
          </div>
          <div class="item-meta">
            <span v-if="item.checkedAt" class="item-time">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10s10-4.5 10-10S17.5 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.2 3.2l.8-1.3l-4.5-2.7V7Z"/></svg>
              {{ formatTime(item.checkedAt) }}
            </span>
            <span v-if="item.checkedBy" class="item-by">por {{ item.checkedBy.name }}</span>
          </div>
        </div>

        <button
          class="btn-undo"
          :title="`Desfazer check-in de ${item.name}`"
          @click="confirmUndo(item)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88c3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>
          <span>Desfazer</span>
        </button>
      </div>
    </div>

    <EmptyState
      v-else-if="confirmedCount === 0"
      title="Nenhum check-in ainda"
      description="Os primeiros confirmados aparecerão aqui."
    />

    <EmptyState
      v-else
      title="Nenhum acampante encontrado"
      description="Ajuste os filtros ou a busca para ver mais resultados."
    />

    <!-- Modal de confirmação -->
    <div class="modal-overlay" v-if="undoTarget" @click.self="undoTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Desfazer check-in?</h2>
        <p class="confirm-text">
          O check-in de <strong>{{ undoTarget.name }}</strong> será desfeito e o acampante voltará para a lista de pendentes.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" @click="undoTarget = null">Cancelar</button>
          <button class="btn-danger" :disabled="undoing" @click="executeUndo">
            <span v-if="!undoing">Desfazer check-in</span>
            <span v-else>Desfazendo...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAcampantesStore } from '../stores/acampantes'
import { EQUIPES } from '../lib/equipes'
import { TRANSPORTES } from '../lib/transportes'
import EquipeChip from '../components/EquipeChip.vue'
import TransporteBadge from '../components/TransporteBadge.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'

function normalize(s) {
  return (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export default {
  name: 'ConfirmedPage',
  components: { EquipeChip, TransporteBadge, SkeletonLoader, EmptyState },
  data() {
    return {
      searchQuery: '',
      transporteFilter: 'ALL',
      equipeFilter: 'ALL',
      transportes: TRANSPORTES,
      equipes: EQUIPES,
      undoTarget: null,
      undoing: false
    }
  },
  computed: {
    store() { return useAcampantesStore() },
    confirmedCount() { return this.store.confirmedCount },
    semEquipeConfirmedCount() { return this.store.confirmed.filter(a => !a.equipe).length },
    filtered() {
      const q = this.searchQuery.trim() ? normalize(this.searchQuery) : null
      const equipeMatch = a => {
        if (this.equipeFilter === 'ALL') return true
        if (this.equipeFilter === 'NONE') return !a.equipe
        return a.equipe === this.equipeFilter
      }
      return this.store.confirmed
        .filter(a => this.transporteFilter === 'ALL' || a.transporte === this.transporteFilter)
        .filter(equipeMatch)
        .filter(a => !q || normalize(a.name).includes(q) || normalize(a.contato).includes(q))
        .slice()
        .sort((a, b) => {
          const da = a.checkedAt ? new Date(a.checkedAt).getTime() : 0
          const db = b.checkedAt ? new Date(b.checkedAt).getTime() : 0
          return db - da
        })
    }
  },
  methods: {
    formatTime(iso) {
      try {
        return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      } catch { return '' }
    },
    confirmUndo(item) {
      this.undoTarget = item
    },
    async executeUndo() {
      if (!this.undoTarget) return
      this.undoing = true
      try {
        await this.store.uncheckin(this.undoTarget.id)
        // Sucesso notificado via Pusher (uncheck-user) no App.vue.
        this.undoTarget = null
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao desfazer check-in'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.undoing = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
  gap: var(--spacing-md);
}
.page-header .page-title { margin-bottom: 0; }
.confirmed-counter {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-sm);
  padding: 6px var(--spacing-base);
  background: rgba(46, 204, 113, 0.12);
  color: var(--color-success);
  border-radius: var(--radius-md);
  font-weight: 700;
}
.counter-num { font-size: var(--text-xl); line-height: 1; }
.counter-label { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.5px; }

.search-box {
  position: relative;
  max-width: 420px;
  margin-bottom: var(--spacing-md);
}
.search-input { padding-left: 38px; padding-right: 38px; }
.search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--color-text-muted); pointer-events: none;
}
.clear-btn {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--color-text-muted);
  padding: 4px; display: inline-flex;
}
.clear-btn:hover { color: var(--color-danger); }

.filters { display: flex; flex-direction: column; gap: var(--spacing-md); margin-bottom: var(--spacing-base); }
.filter-row { display: flex; align-items: center; gap: var(--spacing-base); flex-wrap: wrap; }
.filter-label {
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--color-text-muted);
  min-width: 78px;
}
.chip-row { display: flex; flex-wrap: wrap; gap: var(--spacing-sm); }
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 6px 14px;
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-bg-separator);
  border-radius: 16px;
  font-size: var(--text-xs);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
}
.filter-chip:hover { border-color: var(--color-accent); color: var(--color-accent); }
.filter-chip.active { background: var(--color-accent); border-color: var(--color-accent); color: #fff; }
.filter-chip-sem-equipe { border-style: dashed; color: var(--color-text-muted); }
.filter-chip-sem-equipe.active { background: var(--color-text-secondary); border-color: var(--color-text-secondary); color: #fff; }

.confirmed-list { display: flex; flex-direction: column; gap: var(--spacing-md); }
.confirmed-item {
  padding: var(--spacing-base);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-base);
}
.item-main { display: flex; flex-direction: column; gap: var(--spacing-sm); flex: 1; min-width: 0; }
.item-name-row { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.item-name { font-size: var(--text-md); font-weight: 600; color: var(--color-text-primary); }
.item-meta {
  display: flex; align-items: center; gap: var(--spacing-base); flex-wrap: wrap;
  font-size: var(--text-xs); color: var(--color-text-muted);
}
.item-time { display: inline-flex; align-items: center; gap: 4px; }

.btn-undo {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 14px;
  background: var(--color-bg-surface);
  color: var(--color-warning);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-undo:hover {
  background: var(--color-warning);
  color: #fff;
}

.confirm-text { font-size: var(--text-sm); color: var(--color-text-secondary); margin-bottom: var(--spacing-base); }

@media (max-width: 600px) {
  .confirmed-item { flex-direction: column; align-items: stretch; }
  .btn-undo { justify-content: center; }
}
</style>
